import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { Ability } from 'ember-can';
import Entry from 'garaje/models/entry';
import Invite from 'garaje/models/invite';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type StateService from 'garaje/services/state';
import { GLOBAL_ADMIN, LOCATION_ADMIN } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

import type { GetCanReviewOrReadBlocklistMatchModel } from './security';

const ALLOWED_ROLES = [GLOBAL_ADMIN, LOCATION_ADMIN];

type SupportedModelName = 'invite' | 'entry';

export default class IdentityVerificationAbility extends Ability {
  @service declare currentAdmin: CurrentAdminService;
  @service declare state: StateService;
  @service declare authz: AuthzService;

  declare model: GetCanReviewOrReadBlocklistMatchModel | undefined;

  get canApprove(): boolean {
    return this.canReview;
  }

  get canDeny(): boolean {
    return this.canReview;
  }

  get canConfigure(): boolean {
    const { roleNames } = this.currentAdmin;
    return isPresent(_intersection(ALLOWED_ROLES, roleNames));
  }

  get canReview(): boolean {
    const { parent } = this.model ?? {};

    if (parent instanceof Entry) return this.#canReview('entry');
    if (parent instanceof Invite) return this.#canReview('invite');

    return this.#canReview('invite') || this.#canReview('entry');
  }

  get canView(): boolean {
    const { parent } = this.model ?? {};

    if (parent instanceof Entry) return this.#canView('entry');
    if (parent instanceof Invite) return this.#canView('invite');

    return this.#canView('invite') || this.#canView('entry');
  }

  #canReview(modelName: SupportedModelName): boolean {
    const permission = {
      invite: Permission.VISITORS_INVITE_VISUAL_COMPLIANCE_APPROVAL_REVIEW,
      entry: Permission.VISITORS_ENTRY_VISUAL_COMPLIANCE_APPROVAL_REVIEW,
    }[modelName];
    return this.authz.hasPermissionAtCurrentLocation(permission);
  }

  #canView(modelName: SupportedModelName): boolean {
    const permission = {
      invite: Permission.VISITORS_INVITE_VISUAL_COMPLIANCE_APPROVAL_READ,
      entry: Permission.VISITORS_ENTRY_VISUAL_COMPLIANCE_APPROVAL_READ,
    }[modelName];
    return this.authz.hasPermissionAtCurrentLocation(permission);
  }
}
